import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../context/MyContext";
import Menu from "../../components/menu/Menu";
import { Mode } from "../tutorV2/types";
import { Back, Chevron } from "../../assets/svgs/icons";
import { LogoDefault } from "../../assets/svgs/logos";
import AirHeader from "../../components/AirHeader";

interface CompanyLinkItem {
  label: string;
  url: string;
}

const COMPANY_LINKS: CompanyLinkItem[] = [
  {
    label: "회사소개",
    url: "https://about.dongascience.com/company/dongascience",
  },
  {
    label: "저작권·콘텐츠 이용안내",
    url: "https://help.dongascience.com/helpinfo/knowledge",
  },
  {
    label: "개인정보 처리방침",
    url: "https://help.dongascience.com/helpinfo/privacy",
  },
  {
    label: "광고안내",
    url: "https://about.dongascience.com/advertise/magazine",
  },
];

const CompanyInfo = () => {
  const navigate = useNavigate();
  const { user, isMobile } = useContext(MyContext);
  const isLoggedIn = !!user?.id;

  const handleBack = () => {
    navigate(-1);
  };

  const handleLinkClick = (url: string) => {
    window.open(url, "_blank");
  };

  // 모바일 레이아웃
  if (isMobile) {
    return (
      <div className="flex flex-col h-full w-full bg-white overflow-hidden">
        {/* Header - 모바일: AirHeader 사용 */}
        <AirHeader type="sub" title="회사 정보" />

        {/* Content Area - flex로 푸터를 하단에 고정 */}
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          {/* Link List */}
          <div className="flex flex-col w-full">
            {COMPANY_LINKS.map((item, index) => (
              <div
                key={index}
                className="relative flex items-center min-h-[48px] pl-4 gap-4 cursor-pointer transition hover:bg-[#f7f7f7]"
                onClick={() => handleLinkClick(item.url)}
              >
                <span className="flex-1 font-medium text-[#212121] text-[14px] tracking-[-0.28px] leading-5">
                  {item.label}
                </span>
                <span className="shrink-0 w-12 h-12 flex items-center justify-center">
                  <Chevron width={24} height={24} />
                </span>
              </div>
            ))}
          </div>

          {/* Spacer - 푸터를 하단으로 밀어냄 */}
          <div className="flex-1" />

          {/* Footer - 하단 고정, 피그마 순서/크기 */}
          <div className="bg-[#f7f7f7] px-5 py-5 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <img
                src="https://img.dongascience.com/air/MSIT.svg"
                alt="과학기술정보통신부"
                className="h-[20px]"
              />
              <img
                src="https://img.dongascience.com/air/NIA_logo.svg"
                alt="NIA 한국지능정보사회진흥원"
                className="h-[14px]"
              />
              <img
                src="https://img.dongascience.com/air/SaaS_verification.svg"
                alt="SaaS 개발 검증 장관상 수상"
                className="h-[20px]"
              />
              <img
                src="https://img.dongascience.com/air/INNO_logo.svg"
                alt="INNO 디지털경영혁신대상 우수상"
                className="h-[20px]"
              />
              <img
                src="https://img.dongascience.com/air/AI_AWARD.svg"
                alt="AI AWARD 교육 분야 대상"
                className="h-[20px]"
              />
            </div>
            <p className="font-normal text-[12px] text-[#888888] tracking-[-0.24px]">
              Copyright © Dongascience. All right reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // PC 레이아웃 (기존 그대로)
  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      {/* Sidebar */}
      {isLoggedIn && <Menu mode={Mode.TUTOR} />}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Header */}
        <header className="relative flex items-center justify-center bg-white w-full h-[56px] md:h-[64px] shrink-0">
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
            aria-label="뒤로가기"
            onClick={handleBack}
          >
            <Back className="w-6 h-6 text-[#212121]" />
          </button>
          <LogoDefault className="w-[96px] h-[24px] md:w-[100px] md:h-[25px]" />
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          {/* Container: padding 32px 16px, gap 32px */}
          <div className="flex flex-col items-center w-full gap-8 px-4 py-8">
            {/* Page Title */}
            <div className="w-full max-w-[800px]">
              <h1 className="font-bold text-[24px] text-[#212121] leading-9">
                회사 정보
              </h1>
            </div>

            {/* Contents: border-top only */}
            <div className="flex flex-col w-full max-w-[800px] border-t border-solid border-[#E2D5FC]">
              {/* Link List */}
              <div className="flex flex-col">
                {COMPANY_LINKS.map((item, index) => (
                  <div
                    key={index}
                    className="relative flex items-center min-h-[48px] pl-4 gap-4 cursor-pointer transition hover:bg-[#f7f7f7]"
                    onClick={() => handleLinkClick(item.url)}
                  >
                    <span className="flex-1 font-medium text-[#212121] text-[14px] tracking-[-0.28px] leading-5">
                      {item.label}
                    </span>
                    <span className="shrink-0 w-12 h-12 flex items-center justify-center">
                      <Chevron width={24} height={24} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Spacer - 푸터를 하단으로 밀어냄 */}
          <div className="flex-1" />

          {/* Footer - 하단 고정 */}
          <div className="w-full flex justify-center">
            <div className="w-full max-w-[800px] bg-[#f7f7f7] px-5 py-5 flex flex-col gap-4 mx-4 mb-8 rounded-lg">
              <div className="flex flex-wrap items-center gap-2">
                <img
                  src="https://img.dongascience.com/air/MSIT.svg"
                  alt="과학기술정보통신부"
                  className="h-[17px]"
                />
                <img
                  src="https://img.dongascience.com/air/NIA_logo.svg"
                  alt="NIA"
                  className="h-[12px]"
                />
                <img
                  src="https://img.dongascience.com/air/SaaS_verification.svg"
                  alt="SaaS 개발 검증"
                  className="h-[17px]"
                />
                <img
                  src="https://img.dongascience.com/air/INNO_logo.svg"
                  alt="INNO"
                  className="h-[18px]"
                />
                <img
                  src="https://img.dongascience.com/air/AI_AWARD.svg"
                  alt="AI AWARD"
                  className="h-[20px]"
                />
              </div>
              <p className="font-normal text-[12px] text-[#888888] tracking-[-0.24px]">
                Copyright © Dongascience. All right reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfo;
